import {CheckboxInput, TextFieldInput} from "./widgets";
import {FeatureRestriction, NonPaymentBlockingType, NumberType, PrivateRoutingType, UpdateUserData} from "@/api";
import {
    FormattedFormRowProps,
    EnumField,
    NumberField,
    SelectField,
    SelectMultipleField,
    MultipleEnumField,
    MultipleLineError,
} from "@dvrt/graphql-widgets";
import OutgoingCallBarringWidget from "@/outgoing-call-barring/OutgoingCallBarringWidget";
import {FieldName, FieldWidgetRenderer} from "./types";
import {VoicemailToEmailEnabledWidget} from "./VoicemailToEmailEnabledWidget";
import TargetSelector, {TargetType} from "@/components/TargetSelector";
import {SecondStageQuery} from "./queries.generated";
import {FormControl, FormHelperText} from "@mui/material";
import {defineMessage, MessageDescriptor} from "react-intl";
import {
    FEATURE_RESTRICTION_MESSAGES,
    NON_PAYMENT_BLOCKING_TYPE_MESSAGES,
    PRIVATE_ROUTING_TYPE_MESSAGES,
} from "@/utils/translations";

export type RowDescriptor<FieldType extends FieldName> = {
    label: MessageDescriptor;
    rowProps?: Omit<FormattedFormRowProps, "children">;
    render: FieldWidgetRenderer<FieldType>;
};

function numberTypeToTargetType(numberType: NumberType): TargetType | undefined {
    switch (numberType) {
        case NumberType.Extension:
            return TargetType.EXTENSION;
        case NumberType.Fax:
            return TargetType.FAX;
        case NumberType.Group:
            return TargetType.GROUP;
        case NumberType.Ivr:
            return TargetType.IVR;
        case NumberType.Gateway:
            return TargetType.GW;
        case NumberType.GroupInternal:
        default:
            console.debug("Unknown number type", numberType);
            return undefined;
    }
}

export const fieldWidgetDescriptors: {[K in keyof UpdateUserData]: RowDescriptor<K>} = {
    displayName: {
        label: defineMessage({id: "displayName", defaultMessage: "Name"}),
        render: TextFieldInput,
    },
    mobileNumber: {
        label: defineMessage({id: "mobileNumber", defaultMessage: "Mobile number"}),
        render: TextFieldInput,
    },
    homeNumber: {
        label: defineMessage({id: "homeNumber", defaultMessage: "Home number"}),
        render: TextFieldInput,
    },
    notificationEmail: {
        label: defineMessage({id: "notificationEmail", defaultMessage: "Notification email"}),
        render: TextFieldInput,
    },
    voicemailToEmailEnabled: {
        label: defineMessage({id: "voicemailToEmailEnabled", defaultMessage: "Send voicemails in email"}),
        render: VoicemailToEmailEnabledWidget,
    },
    pin: {
        label: defineMessage({id: "pin", defaultMessage: "Pin code"}),
        render: TextFieldInput,
    },
    alternateCallerNumberId: {
        label: defineMessage({id: "alternateCallerNumber", defaultMessage: "Override caller number to"}),
        render: ({disabled, data: {overrideCallerNumbers}, updateValue, value}) => (
            <TargetSelector
                selected={value}
                handleChange={updateValue}
                targets={overrideCallerNumbers.map((item: SecondStageQuery["overrideNumbers"][number]) => ({
                    numberId: item.numberId,
                    displayName: item.numberType === NumberType.Fax ? undefined : item.name.split(" - ")[1],
                    shortNumber: item.name.split(" - ")[0],
                    type: numberTypeToTargetType(item.numberType),
                }))}
                disabled={disabled}
                style={{width: "100%"}}
            />
        ),
    },
    alias: {
        label: defineMessage({id: "alias", defaultMessage: "Alias"}),
        render: TextFieldInput,
    },
    locationId: {
        label: defineMessage({id: "location.label", defaultMessage: "Location"}),
        render: ({data, disabled, updateValue, value, errors, auth, extensionContextFilter}) => (
            <FormControl error={errors.length > 0}>
                <SelectField
                    disabled={disabled || !auth.hasPermission("add_extension", extensionContextFilter)}
                    value={value}
                    onChange={(value) => updateValue(value)}
                    options={data.locations}
                />
                <FormHelperText>
                    <MultipleLineError errors={errors} />
                </FormHelperText>
            </FormControl>
        ),
    },
    featureRestrictions: {
        label: defineMessage({id: "featureRestrictions", defaultMessage: "Feature restrictions"}),
        render: ({disabled, updateValue, value}) => (
            <MultipleEnumField
                disabled={disabled}
                value={value}
                enum_={{FeatureRestriction}}
                onChange={(value) => updateValue(value as FeatureRestriction[])}
                translations={FEATURE_RESTRICTION_MESSAGES}
            />
        ),
    },
    hotNumber: {
        label: defineMessage({id: "hotNumber", defaultMessage: "Hot number"}),
        render: TextFieldInput,
    },
    secretaryNumberId: {
        label: defineMessage({id: "secretaryNumber", defaultMessage: "Secretary number"}),
        render: ({data: {secretaryNumbers}, disabled, updateValue, value}) => {
            return (
                <TargetSelector
                    selected={value}
                    handleChange={(numberId) => updateValue(numberId)}
                    targets={secretaryNumbers.map((sec) => ({
                        numberId: sec.numberId,
                        displayName: sec.name.split(" - ")[1],
                        shortNumber: sec.name.split(" - ")[0],
                        type: TargetType.EXTENSION,
                    }))}
                    disabled={disabled}
                    style={{width: "100%"}}
                />
            );
        },
    },
    groupMembershipGroupIds: {
        label: defineMessage({id: "groupMemberships", defaultMessage: "Group memberships"}),
        render: ({data: {groups}, disabled, form, updateValue}) => (
            <SelectMultipleField
                disabled={disabled}
                value={form.values.groupMembershipGroupIds}
                onChange={(value) => updateValue(value)}
                options={groups}
            />
        ),
    },
    callWaiting: {
        label: defineMessage({id: "callWaiting", defaultMessage: "Call waiting"}),
        render: CheckboxInput,
    },
    previewFeaturesEnabled: {
        label: defineMessage({id: "previewFeaturesEnabled", defaultMessage: "Activate preview features"}),
        render: CheckboxInput,
    },
    cliro: {
        label: defineMessage({id: "cliro", defaultMessage: "Calling line identification restriction override"}),
        render: CheckboxInput,
    },
    connectedNumberPresentationRestrictionOverride: {
        label: defineMessage({
            id: "connectedNumberPresentationRestrictionOverride",
            defaultMessage: "Connected number presentation restriction override",
        }),
        render: CheckboxInput,
    },
    nationalCac: {
        label: defineMessage({id: "nationalCac", defaultMessage: "National Carrier Access Code"}),
        render: TextFieldInput,
    },
    internationalCac: {
        label: defineMessage({id: "internationalCac", defaultMessage: "International Carrier Access Code"}),
        render: TextFieldInput,
    },
    callLimit: {
        label: defineMessage({id: "callLimit", defaultMessage: "Parallel call limit"}),
        render: ({disabled, updateValue, value, errors}) => (
            <NumberField
                disabled={disabled}
                id="callLimit"
                margin="dense"
                fullWidth
                variant="outlined"
                value={value}
                min={0}
                update={(value) => updateValue(value)}
                errors={errors}
            />
        ),
    },
    maliciousCallTrace: {
        label: defineMessage({id: "maliciousCallTrace", defaultMessage: "Malicious call trace"}),
        render: CheckboxInput,
    },
    directInwardDialing: {
        label: defineMessage({id: "directInwardDialing", defaultMessage: "Direct inward dialing"}),
        render: CheckboxInput,
    },
    directOutwardDialing: {
        label: defineMessage({id: "directOutwardDialing", defaultMessage: "Direct outward dialing"}),
        render: CheckboxInput,
    },
    privateRoutingType: {
        label: defineMessage({id: "PrivateRoutingType.label", defaultMessage: "Custom routing type"}),
        render: ({disabled, updateValue, value}) => (
            <EnumField
                disabled={disabled}
                value={value}
                enum_={{PrivateRoutingType}}
                onChange={(val) => updateValue(val as PrivateRoutingType)}
                translations={PRIVATE_ROUTING_TYPE_MESSAGES}
            />
        ),
    },
    nonPaymentBlockingType: {
        label: defineMessage({id: "NonPaymentBlockingType.label", defaultMessage: "Non-payment blocking"}),
        render: ({disabled, updateValue, value}) => (
            <EnumField
                disabled={disabled}
                value={value}
                enum_={{NonPaymentBlockingType}}
                onChange={(val) => updateValue(val as NonPaymentBlockingType)}
                translations={NON_PAYMENT_BLOCKING_TYPE_MESSAGES}
            />
        ),
    },
    callOriginationDenied: {
        label: defineMessage({id: "callOriginationDenied", defaultMessage: "Call origination denied"}),
        render: CheckboxInput,
    },
    callTerminationDenied: {
        label: defineMessage({id: "callTerminationDenied", defaultMessage: "Call termination denied"}),
        render: CheckboxInput,
    },
    outgoingCallBarringOperator: {
        label: defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"}),
        rowProps: {
            labelAlign: "top",
        },
        render: ({form, auth, externalDisabled, extensionContextFilter, data: {domainCallBarrings}}) => {
            return (
                <OutgoingCallBarringWidget
                    masterBarrings={domainCallBarrings}
                    operatorBarrings={form.values.outgoingCallBarringOperator}
                    adminBarrings={form.values.outgoingCallBarringAdmin}
                    userBarrings={form.values.outgoingCallBarringUser}
                    updateOperatorBarrings={(barrings) => form.updateValues({outgoingCallBarringOperator: barrings})}
                    updateAdminBarrings={(barrings) => form.updateValues({outgoingCallBarringAdmin: barrings})}
                    updateUserBarrings={(barrings) => form.updateValues({outgoingCallBarringUser: barrings})}
                    disabledAdmin={
                        !auth.hasPermission("edit_admin_extension", extensionContextFilter) || externalDisabled
                    }
                    disabledOperator={
                        !auth.hasPermission("edit_superuser_extension", extensionContextFilter) || externalDisabled
                    }
                    disabledUser={externalDisabled}
                />
            );
        },
    },
    outgoingCallBarringUser: {
        label: defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"}),
        rowProps: {
            labelAlign: "top",
        },
        render: ({form, disabled, data: {domainCallBarrings}}) => (
            <OutgoingCallBarringWidget
                masterBarrings={domainCallBarrings}
                operatorBarrings={form.values.outgoingCallBarringOperator}
                adminBarrings={form.values.outgoingCallBarringAdmin}
                userBarrings={form.values.outgoingCallBarringUser}
                updateUserBarrings={(barrings) => form.updateValues({outgoingCallBarringUser: barrings})}
                disabledUser={disabled}
            />
        ),
    },
};
